import { createConfig, fallback, http } from "wagmi";
import { base } from "wagmi/chains";
import { coinbaseWallet, injected, walletConnect } from "wagmi/connectors";
import { createPublicClient } from "viem";

/*
 * Wagmi config for the boon app.
 *
 * `preference` uses the OBJECT form (`{ options: "all" }`).
 * The string-only form (`preference: "all"`) is deprecated
 * since wagmi 2.12.33 / @coinbase/wallet-sdk v4 and produces a runtime
 * warning. Keep the object form even if the only key today is `options`.
 *
 * The quick-send and recipient claim paths are wallet-agnostic: MetaMask,
 * Phantom, Rabby, another injected wallet, WalletConnect, or Coinbase Wallet /
 * Base Account can approve/send or receive USDC.
 * Register explicit injected connectors before the generic injected connector
 * so the auction page can reconnect silently to the last chosen extension on a
 * reload. In particular, Coinbase Wallet SDK reconnect is not silent: it can
 * show the "Which wallet would you like to use?" chooser. Prefer Coinbase's
 * injected provider when it exists, and keep the SDK only as an explicit
 * connect fallback for users without the extension.
 * Register MetaMask explicitly with wagmi's injected target before the generic
 * injected connector so a MetaMask click cannot route through Coinbase,
 * Phantom, or an EIP-6963 wallet chooser. Do not use the MetaMask SDK here:
 * extension users need a direct provider, while mobile users can use
 * WalletConnect.
 * Register Phantom explicitly via wagmi's `target: "phantom"` injected
 * connector so the UI can show a real Phantom choice instead of asking people
 * to click a MetaMask-labelled path and hope Phantom intercepts it.
 *
 * WalletConnect is only registered when VITE_WC_PROJECT_ID is set. The Project
 * ID is public client metadata from the Reown/WalletConnect dashboard, not a
 * secret, but builds without it should not render a dead "Other wallet" modal.
 *
 * NOTE: browser wallets can call tip / tipAgent / tipPrivate directly from
 * this SPA. The CLI + OWS path is for autonomous-agent-to-agent loops, not
 * a precondition for sending to an ERC-8004 agent recipient.
 */

const DEFAULT_BASE_RPC_URLS = [
  "https://base-rpc.publicnode.com",
  "https://1rpc.io/base",
] as const;

function splitRpcUrls(value: string | undefined): string[] {
  return (value ?? "")
    .split(",")
    .map((url) => url.trim())
    .filter(Boolean);
}

const configuredBaseRpcUrls = [
  ...splitRpcUrls(import.meta.env.VITE_BASE_RPC_URL as string | undefined),
  ...splitRpcUrls(import.meta.env.VITE_BASE_RPC_FALLBACK_URL as string | undefined),
  ...splitRpcUrls(import.meta.env.VITE_BASE_RPC_FALLBACK_URLS as string | undefined),
];

const baseRpcUrls = Array.from(new Set([...configuredBaseRpcUrls, ...DEFAULT_BASE_RPC_URLS]));

const baseTransport =
  baseRpcUrls.length > 1
    ? fallback(
        baseRpcUrls.map((url, index) =>
          http(url, {
            key: `base-rpc-${index}`,
            retryCount: 1,
            timeout: 10_000,
          }),
        ),
        { rank: true, retryCount: 1 },
      )
    : http(baseRpcUrls[0]);

const walletConnectProjectId = (import.meta.env.VITE_WC_PROJECT_ID as string | undefined)?.trim();
const walletConnectConnector = walletConnectProjectId
  ? walletConnect({
      projectId: walletConnectProjectId,
      metadata: {
        name: "Boon",
        description: "Onchain USDC thank-yous for the people who helped you.",
        url: "https://boonprotocol.com",
        icons: ["https://boonprotocol.com/favicon.svg"],
      },
      showQrModal: true,
    })
  : null;

export const config = createConfig({
  chains: [base],
  connectors: [
    injected({ target: "metaMask", shimDisconnect: true, unstable_shimAsyncInject: 1_000 }),
    injected({ target: "coinbaseWallet", shimDisconnect: true, unstable_shimAsyncInject: 1_000 }),
    injected({ target: "phantom", shimDisconnect: true, unstable_shimAsyncInject: 1_000 }),
    injected({ shimDisconnect: true }),
    ...(walletConnectConnector ? [walletConnectConnector] : []),
    coinbaseWallet({
      appName: "boon",
      // `eoaOnly`, not `all`: the Coinbase Wallet SDK v4 Smart Wallet popup
      // flow hangs (or throws `hashFuncMap[method] is not a function`) on
      // EIP-712 `signTypedData` — the exact call the x402 review signer makes
      // (wagmi #4414). The EOA/extension path signs typed data reliably, and
      // Boon's flows already assume a Base EOA holding USDC/$BOON.
      preference: { options: "eoaOnly" },
    }),
  ],
  transports: {
    [base.id]: baseTransport,
  },
});

declare module "wagmi" {
  interface Register {
    config: typeof config;
  }
}

// Dedicated client for historical eth_getLogs RANGE scans (auction settlement
// resolution scans thousands of blocks). The wallet/read `baseTransport` above
// is a RANKED fallback that can route a request to RPCs that are wrong for wide
// log ranges:
//   - Alchemy's public RPC caps eth_getLogs at 100 blocks -> hard error on a
//     multi-thousand-block window;
//   - 1rpc.io can return an EMPTY result for a valid range without erroring,
//     which viem accepts as success -> the scan silently misses the logs.
// Either one, hit on a given page load, makes a settled round render as
// "settling" intermittently. This client uses ONLY RPCs that reliably serve
// >=10k-block getLogs, in deterministic priority order (no rank), so the scan
// is stable across reloads. Operators can prepend a paid archival endpoint via
// VITE_BASE_LOG_RPC_URL.
const logScanRpcUrls = Array.from(
  new Set(
    [
      (import.meta.env.VITE_BASE_LOG_RPC_URL as string | undefined)?.trim(),
      "https://base-rpc.publicnode.com",
      "https://mainnet.base.org",
    ].filter((url): url is string => Boolean(url)),
  ),
);

export const baseLogScanClient = createPublicClient({
  chain: base,
  transport:
    logScanRpcUrls.length > 1
      ? fallback(
          logScanRpcUrls.map((url, index) =>
            http(url, { key: `base-log-${index}`, retryCount: 2, timeout: 12_000 }),
          ),
          // No `rank`: keep deterministic priority order (publicnode, then
          // base.org) so getLogs never gets routed to a 100-block-capped RPC.
          { retryCount: 1 },
        )
      : http(logScanRpcUrls[0]),
});
